import { EventEmitter } from "events";
import RealtimeService from "../services/RealtimeService";
import RealtimeServiceFactory from "../services/RealtimeServiceFactory";
import { Call } from "../types";
import StoreContainer from "../stores/StoreContainer";
import CallService from "../services/CallService";
import { CallRequest } from "../types";
import CookieStore from "./CookieStore";
import VisitorService from "../services/VisitorService";
import VisitorStore from "./VisitorStore";

class CallStore {
  private _emitter = new EventEmitter();
  private _agentName: string;
  private _agentHeadshotUrl: string;
  private _call: Call;
  private _realtimeService: RealtimeService;
  private _callId: string;
  private _cookieStore: CookieStore;

  constructor(cookieStore: CookieStore) {
    this._cookieStore = cookieStore;
    this._callId = this._cookieStore.get("callId");
  }

  async connect() {
    this._emitter.emit("connecting");
    this._realtimeService = RealtimeServiceFactory.getInstance();
    this._realtimeService.onReceiveEndCall(this.onReceiveEndCall.bind(this));
    this._realtimeService.onReceiveCall(this.onReceiveCall.bind(this));
    await this._realtimeService.startConnection();
    this._emitter.emit("connected");
  }

  createCallRequest() {
    const data: CallRequest = {
      SiteID: StoreContainer.SettingStore.getSiteId(),
      GroupID: StoreContainer.GroupStore.getActiveOrPageGroupId(),
      VisitorID: StoreContainer.VisitorStore.getVisitorId(),
      Name: "",
      Email: "",
      FromEngagementId: StoreContainer.EngagementStore.getEngagementId(),
      IsPolling: false,
      InviteConversionID: null,
      RequestedFrom: "",
      GroupAvailabilityStatus: 3,
      IntroMessage: "",
      CustomData: StoreContainer.SettingStore.getCustomVars(),
      CallId: this.getCallId(),
      VisitorInfo: StoreContainer.VisitorStore.getVisitorInfo(),
      DirectPhoneNumber: ""
    };

    return data;
  }

  async loadCall() {
    const call = await CallService.loadCall(
      StoreContainer.SettingStore.getSiteId(),
      this.createCallRequest()
    );
    if (call === null) {
      this.clearCallInfo();
      await StoreContainer.FrameStore.collapse();
      return;
    }

    this._call = call;
    this.connect();
    this.onReceiveCall(this._call);
  }

  setCallId(callId: string) {
    this._callId = callId;
    this._cookieStore.set("callId", callId);
  }

  hasCallCookie() {
    return this._cookieStore.get("callId");
  }

  async close() {
    await this.clearCallInfo();
  }

  async handleCallWaiting(call: Call) {
    this._call = call;
    this._emitter.emit("callWaiting");
  }

  clearCallInfo() {
    this.setCallId(null);
    this._call = undefined;
  }

  onCallWaiting(callback: () => void) {
    this._emitter.addListener("callWaiting", callback);
  }

  onCallReceived(callback: (call: Call) => void) {
    this._emitter.addListener("call", callback);
  }

  onReceiveEndCall(content: any) {
    StoreContainer.ChatStore.handleReceiveEngagementContent(content);
    this._realtimeService.stopConnection();
  }

  onReceiveCall(call: Call) {
    this._emitter.emit("call", call);
  }

  visitorEndCall() {
    this._realtimeService.visitorLeaveCall();
  }

  hasCall() {
    return this._call && this._call.CallId;
  }

  getCall() {
    return this._call;
  }

  getAgentName() {
    return this._agentName;
  }

  getCallId() {
    return this._callId;
  }

  getAgentHeadshotUrl() {
    return this._agentHeadshotUrl;
  }

  onConnecting(callback: () => void) {
    this._emitter.addListener("connecting", callback);
  }

  onConnected(callback: () => void) {
    this._emitter.addListener("connected", callback);
  }

  removeAllListeners() {
    this._emitter.removeAllListeners();
  }
}

export default CallStore;
